"use client";

import { useLocale } from 'next-intl';
import { usePathname } from '@/navigation';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Globe } from 'lucide-react';
import { useMemo } from 'react';
import { capturePostHogEvent } from "@/lib/posthog";
import { locales as supportedLocales, type Locale } from '@/i18n';

// Display metadata per locale. Keep keys in sync with `locales` in i18n.ts.
const LANGUAGE_META: Record<Locale, { name: string; flag: string }> = {
  nl: { name: 'Nederlands', flag: '🇳🇱' },
  en: { name: 'English', flag: '🇬🇧' },
  fr: { name: 'Français', flag: '🇫🇷' },
  es: { name: 'Español', flag: '🇪🇸' },
  tr: { name: 'Türkçe', flag: '🇹🇷' },
};

const languages = supportedLocales.map((code) => ({
  code,
  ...LANGUAGE_META[code],
}));

interface LanguageSwitcherProps {
  size?: 'sm' | 'default';
}

export function LanguageSwitcher({ size = 'default' }: LanguageSwitcherProps) {
  const locale = useLocale();
  const pathname = usePathname();

  const currentLanguage = languages.find((lang) => lang.code === locale) || languages[0];

  // Get the pathname without locale prefix
  // usePathname from next-intl returns pathname without locale prefix
  // But we need to handle the case where it might include the locale
  const pathWithoutLocale = useMemo(() => {
    if (!pathname) return '/';
    
    // usePathname from next-intl should already return pathname without locale
    // But if it doesn't, remove locale prefix
    for (const loc of supportedLocales) {
      if (pathname === `/${loc}` || pathname === `/${loc}/`) {
        return '/';
      }
      if (pathname.startsWith(`/${loc}/`)) {
        return pathname.slice(`/${loc}`.length) || '/';
      }
    }
    
    // If pathname doesn't start with a locale, return it as is
    return pathname;
  }, [pathname]);

  const handleLanguageChange = (newLocale: string) => {
    capturePostHogEvent("language_switched", { from_locale: locale, to_locale: newLocale });
    // Construct the full URL with the new locale
    // This ensures a full page reload with the new locale's messages
    const newPath = pathWithoutLocale === '/' 
      ? `/${newLocale}` 
      : `/${newLocale}${pathWithoutLocale}`;
    
    // Preserve query parameters when switching languages
    const searchParams = typeof window !== 'undefined' ? window.location.search : '';
    const fullPath = searchParams ? `${newPath}${searchParams}` : newPath;
    
    // Use window.location for a full page reload to fetch new locale messages
    window.location.href = fullPath;
  };

  const isSmall = size === 'sm';
  
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        {/* suppressHydrationWarning: Radix generates aria-controls pointing to
            DropdownMenuContent's ID; the useId() counter can differ between SSR
            and client for dynamic-loaded components, causing a benign mismatch. */}
        <Button
          variant="ghost"
          size={isSmall ? "sm" : "sm"}
          className={isSmall ? "gap-1 h-8 px-2" : "gap-2"}
          aria-label="Switch language"
          suppressHydrationWarning
        >
          <Globe className={isSmall ? "h-3.5 w-3.5" : "h-4 w-4"} />
          {!isSmall && (
            <>
              <span className="hidden sm:inline">{currentLanguage.flag} {currentLanguage.name}</span>
              <span className="sm:hidden">{currentLanguage.flag}</span>
            </>
          )}
          {isSmall && (
            <span className="text-lg">{currentLanguage.flag}</span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {languages.map((language) => (
          <DropdownMenuItem
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            className={locale === language.code ? 'bg-muted' : ''}
          >
            <span className="mr-2">{language.flag}</span>
            {language.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

